"use client";

import * as React from "react";
import { CheckCircle2, Clock } from "lucide-react";
import { Tooltip, Button, Progress } from "@heroui/react";
import { cn } from "@/lib/utils";
import {
  ChallengeCampaignEventState,
  CompleteTaskMutation,
  TaskStatus,
} from "@/types/challenges.types";
import { motion, AnimatePresence } from "framer-motion";

export interface QuestButtonProps {
  asChild?: boolean;
  task: TaskStatus;
  buttonText: string;
  taskMutation: CompleteTaskMutation;
  onClick?: () => void;
  className?: string;
}

const LoadingCard = () => (
  <motion.div
    className="w-full"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.2 }}
  >
    <div className="bg-[#0C1113] border border-gray-800/50 rounded-2xl p-4">
      <div className="flex items-center justify-center gap-3">
        <div className="w-5 h-5 border-2 border-[#60FF8A] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-white font-medium">
          Processing your quest...
        </p>
      </div>
    </div>
  </motion.div>
);

const CompletedCard = ({ points }: { points: string }) => (
  <motion.div
    className="w-full"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.2 }}
  >
    <div className="bg-[#0C1113] border border-gray-800/50 rounded-2xl p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#60FF8A]/10 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-[#60FF8A]" />
          </div>
          <div>
            <p className="text-sm text-white font-medium">
              Quest completed successfully!
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-[#60FF8A]">+{points}</span>
          <div className="w-4 h-4 bg-[#60FF8A] rounded-sm" />
        </div>
      </div>
    </div>
  </motion.div>
);

const PendingCard = () => (
  <motion.div
    className="w-full"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.2 }}
  >
    <div className="bg-[#0C1113] border border-gray-800/50 rounded-2xl p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-yellow-500/10 rounded-lg">
          <Clock className="w-5 h-5 text-yellow-500" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-white font-medium">
            Quest pending approval
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Your submission is being reviewed by our team
          </p>
        </div>
      </div>
    </div>
  </motion.div>
);

const CooldownCard = ({ remainingTime }: { remainingTime: number }) => {
  const [timeLeft, setTimeLeft] = React.useState(remainingTime);
  const totalTime = remainingTime;
  const progress = ((totalTime - timeLeft) / totalTime) * 100;

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = Math.max(0, prev - 1000);
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const seconds = Math.ceil(timeLeft / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="bg-[#0C1113] border border-gray-800/50 rounded-2xl p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <p className="text-sm text-white font-medium">
                Cooldown active. Please wait before attempting again.
              </p>
            </div>
          </div>
          <span className="text-lg font-bold text-white tabular-nums">
            {minutes > 0
              ? `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
              : `${seconds}s`}
          </span>
        </div>
        <Progress
          size="sm"
          value={progress}
          className="mt-3"
          classNames={{
            indicator: "bg-gradient-to-r from-gray-400 to-gray-500",
          }}
        />
      </div>
    </motion.div>
  );
};

export const QuestTaskButton = React.forwardRef<
  HTMLButtonElement,
  QuestButtonProps
>(({ taskMutation, task, buttonText, className, onClick }, ref) => {
  const [remainingTime, setRemainingTime] = React.useState(
    Math.max(0, (task.cooldownEndsAt || 0) - Date.now())
  );

  React.useEffect(() => {
    const timer = setInterval(() => {
      const newTime = Math.max(0, (task.cooldownEndsAt || 0) - Date.now());
      setRemainingTime(newTime);
    }, 1000);

    return () => clearInterval(timer);
  }, [task.cooldownEndsAt]);

  const isCooldownOver = remainingTime <= 0;

  // Use mutation state for loading (like Linera)
  const loading = taskMutation.isPending;
  const completed = task.status === ChallengeCampaignEventState.SUCCESSFUL;
  const pendingReview = task.status === ChallengeCampaignEventState.PENDING;
  const showCooldown = !isCooldownOver && remainingTime > 0;

  const disabled =
    task.status === ChallengeCampaignEventState.PENDING ||
    task.status === ChallengeCampaignEventState.SUCCESSFUL ||
    !isCooldownOver ||
    taskMutation.isPending;

  const tooltipText = React.useMemo(() => {
    if (pendingReview && !loading) {
      return "Task is under review";
    }
    if (!isCooldownOver) {
      return `Cannot attempt yet, try in ${Math.ceil(
        remainingTime / 1000
      )} seconds`;
    }
    return null;
  }, [pendingReview, loading, isCooldownOver, remainingTime]);

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <LoadingCard />
      ) : completed ? (
        <CompletedCard points={task.pointsReceived} />
      ) : pendingReview ? (
        <PendingCard />
      ) : showCooldown ? (
        <CooldownCard remainingTime={remainingTime} />
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Tooltip content={tooltipText} isDisabled={!tooltipText} showArrow>
            <Button
              isDisabled={disabled}
              ref={ref}
              size="lg"
              onPress={() => {
                if (onClick) {
                  onClick();
                }
              }}
              className={cn(
                "relative overflow-hidden group flex items-center justify-center",
                "bg-[#60FF8A] hover:bg-[#60FF8A]/90",
                "text-black font-bold px-8 py-4 rounded-2xl",
                "transition-all duration-300 transform",
                "shadow-lg hover:shadow-xl",
                disabled && "opacity-50 cursor-not-allowed",
                className
              )}
            >
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
              <div className="relative flex items-center justify-center gap-2">
                {buttonText || "Start Quest"}
              </div>
            </Button>
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

QuestTaskButton.displayName = "QuestTaskButton";
